
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  subnav: true,
  maxTocLevel: 4,
  layout: 'reference',
  secondLevelToc: true,
  alwaysExpand: true,
  parentTocLevel: 3,
  childTocLevel: 4,
  description: 'API reference for endpoints and webhooks associated with Plaid Check.',
  source: 'endpoint'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const Schema = makeShortcode("Schema");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Plaid Check' subtitle='API reference for Plaid Check endpoints and webhooks' compact mdxType="Header" />
    <Callout warning mdxType="Callout">
      <p>{`The endpoints on this page will be available starting October 30, 2026. Until then, use the `}<a parentName="p" {...{
          "href": "/docs/api/products/check-legacy/"
        }}>{`legacy Plaid Check endpoints`}</a>{`.`}</p>
    </Callout>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Endpoints`}</th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/cra/report/get`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Retrieve a Consumer Report for your user`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/cra/report/create`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Generate an updated Consumer Report for your user`}</td>
        </tr>
      </tbody>
    </table>
    <Callout mdxType="Callout">
      <p>{`This page documents the latest suite of Plaid Check APIs. For the legacy `}<inlineCode parentName="p">{`/cra/check_report/*`}</inlineCode>{`, `}<inlineCode parentName="p">{`/cra/servicing/*`}</inlineCode>{`, and `}<inlineCode parentName="p">{`/cra/monitoring_insights/*`}</inlineCode>{` endpoints, see the `}<a parentName="p" {...{
          "href": "/docs/api/products/check-legacy/"
        }}>{`Legacy Plaid Check API reference`}</a>{`.`}</p>
    </Callout>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`See also`}</th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/link/token/create`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Create a token for initializing a Link session with Plaid Check`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/user/create`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Create a user for use with Plaid Check`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/user/update`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Update an existing user token to work with Plaid Check, or change user details`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/user/items/get`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Returns Items associated with a user along with their corresponding statuses`}</td>
        </tr>
      </tbody>
    </table>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Webhooks`}</th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "#cra_report_ready"
            }}><inlineCode parentName="a">{`CRA_REPORT_READY`}</inlineCode></a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A Consumer Report is ready to be retrieved`}</td>
        </tr>
      </tbody>
    </table>
    <h4 {...{
      "id": "crareportget"
    }}>{`/cra/report/get`}</h4>
    <Schema method='POST' route='/cra/report/get' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/cra/report/get' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/cra/report/get \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "user_id": "usr_9nSp2KuZ2x4JDw",
  "scope": "PLAID_NETWORK",
  "report_id": "cra-report-production-6f8b1e0c-4d2a-4c7e-9a3b-2f1d8e5a7c90",
  "decision_stage": "DECISIONING",
  "consumer_report_permissible_purpose": "EXTENSION_OF_CREDIT",
  "products": [
    { "product": "cra_lend_score", "version": "V2" },
    { "product": "cra_cashflow_insights", "version": "V2" },
    { "product": "cra_network_insights", "version": "V2" }
  ]
}'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`try {
  const response = await client.craReportGet({
    user_id: 'usr_9nSp2KuZ2x4JDw',
    scope: 'PLAID_NETWORK',
    report_id: 'cra-report-production-6f8b1e0c-4d2a-4c7e-9a3b-2f1d8e5a7c90',
    decision_stage: 'DECISIONING',
    consumer_report_permissible_purpose: 'EXTENSION_OF_CREDIT',
    products: [
      { product: 'cra_lend_score', version: 'V2' },
      { product: 'cra_cashflow_insights', version: 'V2' },
      { product: 'cra_network_insights', version: 'V2' },
    ],
  });
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = CraReportGetRequest(
  user_id='usr_9nSp2KuZ2x4JDw',
  scope=CraReportScope('PLAID_NETWORK'),
  report_id='cra-report-production-6f8b1e0c-4d2a-4c7e-9a3b-2f1d8e5a7c90',
  decision_stage=CraReportDecisionStage('DECISIONING'),
  consumer_report_permissible_purpose=ConsumerReportPermissiblePurpose('EXTENSION_OF_CREDIT'),
  products=[
    CraReportGetRequestProduct(CraLendScoreProduct(product='cra_lend_score', version='V2')),
    CraReportGetRequestProduct(CraCashflowInsightsProduct(product='cra_cashflow_insights', version='V2')),
    CraReportGetRequestProduct(CraNetworkInsightsProduct(product='cra_network_insights', version='V2'))
  ]
)

response = client.cra_report_get(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::CraReportGetRequest.new(
  {
    user_id: 'usr_9nSp2KuZ2x4JDw',
    scope: 'PLAID_NETWORK',
    report_id: 'cra-report-production-6f8b1e0c-4d2a-4c7e-9a3b-2f1d8e5a7c90',
    decision_stage: 'DECISIONING',
    consumer_report_permissible_purpose: 'EXTENSION_OF_CREDIT',
    products: [
      { product: 'cra_lend_score', version: 'V2' },
      { product: 'cra_cashflow_insights', version: 'V2' },
      { product: 'cra_network_insights', version: 'V2' }
    ]
  }
)

response = client.cra_report_get(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`CraReportGetRequest request = new CraReportGetRequest()
  .userId("usr_9nSp2KuZ2x4JDw")
  .scope(CraReportScope.PLAID_NETWORK)
  .reportId("cra-report-production-6f8b1e0c-4d2a-4c7e-9a3b-2f1d8e5a7c90")
  .decisionStage(CraReportDecisionStage.DECISIONING)
  .consumerReportPermissiblePurpose(ConsumerReportPermissiblePurpose.EXTENSION_OF_CREDIT)
  .products(List.of(
    new CraReportGetRequestProduct(new CraLendScoreProduct()
      .product(CraLendScoreProduct.ProductEnum.CRA_LEND_SCORE)
      .version("V2")),
    new CraReportGetRequestProduct(new CraCashflowInsightsProduct()
      .product(CraCashflowInsightsProduct.ProductEnum.CRA_CASHFLOW_INSIGHTS)
      .version("V2")),
    new CraReportGetRequestProduct(new CraNetworkInsightsProduct()
      .product(CraNetworkInsightsProduct.ProductEnum.CRA_NETWORK_INSIGHTS)
      .version("V2"))));

Response<CraReportGetResponse> response = client
  .craReportGet(request)
  .execute();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewCraReportGetRequest(
  "usr_9nSp2KuZ2x4JDw",
  []plaid.CraReportGetRequestProduct{
    plaid.CraLendScoreProductAsCraReportGetRequestProduct(plaid.NewCraLendScoreProduct("cra_lend_score", "V2")),
    plaid.CraCashflowInsightsProductAsCraReportGetRequestProduct(plaid.NewCraCashflowInsightsProduct("cra_cashflow_insights", "V2")),
    plaid.CraNetworkInsightsProductAsCraReportGetRequestProduct(plaid.NewCraNetworkInsightsProduct("cra_network_insights", "V2")),
  },
  plaid.CRAREPORTDECISIONSTAGE_DECISIONING,
  plaid.CONSUMERREPORTPERMISSIBLEPURPOSE_EXTENSION_OF_CREDIT,
)
request.SetScope(plaid.CRAREPORTSCOPE_PLAID_NETWORK)
request.SetReportId("cra-report-production-6f8b1e0c-4d2a-4c7e-9a3b-2f1d8e5a7c90")

response, _, err := client.PlaidApi.
  CraReportGet(ctx).
  CraReportGetRequest(*request).
  Execute()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`request := &plaid.CraReportGetRequest{
    ConsumerReportPermissiblePurpose: plaid.ConsumerReportPermissiblePurposeExtensionOfCredit,
    DecisionStage:                    plaid.CraReportDecisionStageDecisioning,
    Products: []*plaid.CraReportGetRequestProduct{
        &plaid.CraReportGetRequestProduct{
            CraLendScore: &plaid.CraLendScoreProduct{
                Version: "V2",
            },
        },
        &plaid.CraReportGetRequestProduct{
            CraCashflowInsights: &plaid.CraCashflowInsightsProduct{
                Version: "V2",
            },
        },
        &plaid.CraReportGetRequestProduct{
            CraNetworkInsights: &plaid.CraNetworkInsightsProduct{
                Version: "V2",
            },
        },
    },
    ReportID: plaid.String("cra-report-production-6f8b1e0c-4d2a-4c7e-9a3b-2f1d8e5a7c90"),
    Scope:    plaid.CraReportScopePlaidNetwork.Ptr(),
    UserID:   "usr_9nSp2KuZ2x4JDw",
}
response, err := client.Cra.Report.Get(ctx, request)
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/cra/report/get' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "crareportcreate"
    }}>{`/cra/report/create`}</h4>
    <Schema method='POST' route='/cra/report/create' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/cra/report/create' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/cra/report/create \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "user_id": "usr_9nSp2KuZ2x4JDw",
  "scope": "CLIENT_USER",
  "webhook": "https://sample-web-hook.com",
  "days_requested": 365,
  "decision_stage": "DECISIONING",
  "consumer_report_permissible_purpose": "EXTENSION_OF_CREDIT",
  "products": [
    { "product": "cra_base_report", "version": "V1" },
    { "product": "cra_income_insights", "version": "V2" },
    { "product": "cra_lend_score", "version": "V2" }
  ]
}'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`try {
  const response = await client.craReportCreate({
    user_id: 'usr_9nSp2KuZ2x4JDw',
    scope: 'CLIENT_USER',
    webhook: 'https://sample-web-hook.com',
    days_requested: 365,
    decision_stage: 'DECISIONING',
    consumer_report_permissible_purpose: 'EXTENSION_OF_CREDIT',
    products: [
      { product: 'cra_base_report', version: 'V1' },
      { product: 'cra_income_insights', version: 'V2' },
      { product: 'cra_lend_score', version: 'V2' },
    ],
  });
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = CraReportCreateRequest(
  user_id='usr_9nSp2KuZ2x4JDw',
  scope=CraReportScope('CLIENT_USER'),
  webhook='https://sample-web-hook.com',
  days_requested=365,
  decision_stage=CraReportDecisionStage('DECISIONING'),
  consumer_report_permissible_purpose=ConsumerReportPermissiblePurpose('EXTENSION_OF_CREDIT'),
  products=[
    CraReportProduct(CraReportCreateBaseReportProductConfig(product='cra_base_report', version='V1')),
    CraReportProduct(CraReportCreateIncomeInsightsProductConfig(product='cra_income_insights', version='V2')),
    CraReportProduct(CraReportCreateLendScoreProductConfig(product='cra_lend_score', version='V2'))
  ]
)

response = client.cra_report_create(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::CraReportCreateRequest.new(
  {
    user_id: 'usr_9nSp2KuZ2x4JDw',
    scope: 'CLIENT_USER',
    webhook: 'https://sample-web-hook.com',
    days_requested: 365,
    decision_stage: 'DECISIONING',
    consumer_report_permissible_purpose: 'EXTENSION_OF_CREDIT',
    products: [
      { product: 'cra_base_report', version: 'V1' },
      { product: 'cra_income_insights', version: 'V2' },
      { product: 'cra_lend_score', version: 'V2' }
    ]
  }
)

response = client.cra_report_create(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`CraReportCreateRequest request = new CraReportCreateRequest()
  .userId("usr_9nSp2KuZ2x4JDw")
  .scope(CraReportScope.CLIENT_USER)
  .webhook("https://sample-web-hook.com")
  .daysRequested(365)
  .decisionStage(CraReportDecisionStage.DECISIONING)
  .consumerReportPermissiblePurpose(ConsumerReportPermissiblePurpose.EXTENSION_OF_CREDIT)
  .products(List.of(
    new CraReportProduct(new CraReportCreateBaseReportProductConfig()
      .product(CraReportCreateBaseReportProductConfig.ProductEnum.CRA_BASE_REPORT)
      .version("V1")),
    new CraReportProduct(new CraReportCreateIncomeInsightsProductConfig()
      .product(CraReportCreateIncomeInsightsProductConfig.ProductEnum.CRA_INCOME_INSIGHTS)
      .version("V2")),
    new CraReportProduct(new CraReportCreateLendScoreProductConfig()
      .product(CraReportCreateLendScoreProductConfig.ProductEnum.CRA_LEND_SCORE)
      .version("V2"))));

Response<CraReportCreateResponse> response = client
  .craReportCreate(request)
  .execute();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewCraReportCreateRequest(
  "usr_9nSp2KuZ2x4JDw",
  []plaid.CraReportProduct{
    plaid.CraReportCreateBaseReportProductConfigAsCraReportProduct(plaid.NewCraReportCreateBaseReportProductConfig("cra_base_report", "V1")),
    plaid.CraReportCreateIncomeInsightsProductConfigAsCraReportProduct(plaid.NewCraReportCreateIncomeInsightsProductConfig("cra_income_insights", "V2")),
    plaid.CraReportCreateLendScoreProductConfigAsCraReportProduct(plaid.NewCraReportCreateLendScoreProductConfig("cra_lend_score", "V2")),
  },
  plaid.CRAREPORTSCOPE_CLIENT_USER,
  plaid.CRAREPORTDECISIONSTAGE_DECISIONING,
  plaid.CONSUMERREPORTPERMISSIBLEPURPOSE_EXTENSION_OF_CREDIT,
)
request.SetWebhook("https://sample-web-hook.com")
request.SetDaysRequested(365)

response, _, err := client.PlaidApi.
  CraReportCreate(ctx).
  CraReportCreateRequest(*request).
  Execute()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`request := &plaid.CraReportCreateRequest{
    ConsumerReportPermissiblePurpose: plaid.ConsumerReportPermissiblePurposeExtensionOfCredit,
    DaysRequested:                    plaid.Int(365),
    DecisionStage:                    plaid.CraReportDecisionStageDecisioning,
    Products: []*plaid.CraReportProduct{
        &plaid.CraReportProduct{
            CraBaseReport: &plaid.CraReportCreateBaseReportProductConfig{
                Version: "V1",
            },
        },
        &plaid.CraReportProduct{
            CraIncomeInsights: &plaid.CraReportCreateIncomeInsightsProductConfig{
                Version: "V2",
            },
        },
        &plaid.CraReportProduct{
            CraLendScore: &plaid.CraReportCreateLendScoreProductConfig{
                Version: "V2",
            },
        },
    },
    Scope:   plaid.CraReportScopeClientUser,
    UserID:  "usr_9nSp2KuZ2x4JDw",
    Webhook: plaid.String("https://sample-web-hook.com"),
}
response, err := client.Cra.Report.Create(ctx, request)
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/cra/report/create' type='response' mdxType="Schema" />
    <h3 {...{
      "id": "webhooks"
    }}>{`Webhooks`}</h3>
    <p>{`When you create a new report, either by creating a Link token with `}<inlineCode parentName="p">{`cra_report_parameter`}</inlineCode>{` or by calling `}<inlineCode parentName="p">{`/cra/report/create`}</inlineCode>{`, Plaid Check will start generating the requested products for you asynchronously. When generation finishes, whether every product succeeded, only some did, or none did, Plaid Check will send you a `}<inlineCode parentName="p">{`CRA_REPORT: CRA_REPORT_READY`}</inlineCode>{` webhook. Its `}<inlineCode parentName="p">{`successful_products`}</inlineCode>{` and `}<inlineCode parentName="p">{`failed_products`}</inlineCode>{` fields indicate which products were generated, and `}<inlineCode parentName="p">{`error_code`}</inlineCode>{` is populated when every product failed. Call `}<inlineCode parentName="p">{`/cra/report/get`}</inlineCode>{` to retrieve the generated products and to see error details for any failed products.`}</p>
    <h4 {...{
      "id": "cra_report_ready"
    }}><inlineCode parentName="h4">{`CRA_REPORT_READY`}</inlineCode></h4>
    <Schema model='CraReportReadyWebhook' type='model' mdxType="Schema" />
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "crareportget",
        level: 4,
        title: "/cra/report/get",
      },
{
        id: "crareportcreate",
        level: 4,
        title: "/cra/report/create",
      },
{
        id: "webhooks",
        level: 3,
        title: "Webhooks",
      },
{
        id: "cra_report_ready",
        level: 4,
        title: "CRA_REPORT_READY",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
